import { Data } from '@fileverse-dev/ddoc/types';
import { IGunInstance, IGunInstanceRoot, IGunUserInstance } from 'gun';
import type { SmartAccountClient } from 'permissionless';
import type { Chain, RpcSchema, Client, Transport } from 'viem';
import { SmartAccount } from 'viem/account-abstraction';

export type TSmartAccountClient = SmartAccountClient<
  Transport,
  Chain,
  SmartAccount,
  Client,
  RpcSchema
>;

export interface PortalCredentials {
  editDID: string;
  editSecret: string;
  viewDID: string;
  viewSecret: string;
}

export interface SendUpdateParams {
  data: string;
  last_published_content_hash: string | null;
}

export interface PortalServerKeys {
  memberDecryptionKey: string;
  memberEncryptionKey: string;
  ownerDecryptionKey: string;
  ownerEncryptionKey: string;
  portalDecryptionKey: string;
  portalEncryptionKey: string;
  portalGunKey: string;
}

export interface IPortal {
  owner: string;
  portalAddress: string;
  serverKeys: PortalServerKeys;
  credentials: PortalCredentials;
}

export type TGunUserNode = IGunUserInstance<
  any,
  any,
  any,
  IGunInstanceRoot<any, IGunInstance<any>>
>;

export interface IDdoc {
  createdAt: number;
  portalAddress: string;
  ddocId: string;
  title: string;
  content: Data['editorJSONData'] | string;
  publishStatus: PublishStatus;
  onChainFileId: number | null;
  lastPublishedContent:
    | string
    | null
    | {
        content: string | JSONContent;
        title: string;
      };
  linkKey: string | null;
  last_updated: number;
  lastPublishStartTime: number | null;
  linkKeyNonce: string | null;
  isDeleted: boolean;
  deletedAt: number | null;
  commentKey: string;
  markedForDeletion: boolean;
  version: string;
  rtcKey: string;
}
export enum AllowedChain {
  Sepolia = 'sepolia',
  Gnosis = 'gnosis',
}

export enum GunNodeKey {
  Portal = 'portal',
  Key = 'key',
  Files = 'files',
  UserSettings = 'user_settings',
  Ddoc = 'ddoc',
  FileData = 'file_data',
  Onchain = 'onchain',
  AutoPublish = 'auto_publish',
  Comments = 'comments',
  Highlights = 'highlights',
  Replies = 'replies',
  Resolve = 'resolve',
  Delete = 'delete',
}

export type JSONContent = {
  type?: string;
  attrs?: Record<string, any>;
  content?: JSONContent[];
  marks?: {
    type: string;
    attrs?: Record<string, any>;
    [key: string]: any;
  }[];
  text?: string;
  [key: string]: any;
};

export enum PublishStatus {
  Unpublished = 'unpublished',
  Published = 'published',
  Failed = 'failed',
  InProgress = 'in_progress',
}

export enum FileTypeEnum {
  PUBLIC,
  PRIVATE,
  GATED,
  MEMBERS_PRIVATE,
}

export interface IUserPortalSecretKeys {
  editSecret: string;
  ownerEncryptionKey: string;
  portalEncryptionKey: string;
  ownerDecryptionKey: string;
}

export enum FrameAppName {
  FARCASTER = 'FARCASTER',
  LENSTER = 'LENSTER',
}

export interface UserArgs {
  ethAddress: string;
  farcasterUsername?: string;
  lensterUsername?: string;
  portalAddress: string;
  fileId: string;
  appName: FrameAppName;
}

export interface CreateUserArgs {
  ethAddress: string;
  farcasterUsername?: string;
  lensterUsername?: string;
}

export interface UpdateActiveLogArgs {
  ethAddress: string;
  portalAddress: string;
  fileId: string;
  appName: FrameAppName;
  startTimeMillis?: number;
  endTimeMillis?: number;
}
export interface IComment {
  author: string;
  message: string;
  createdAt: number;
  highlightedTextContent?: string;
  isEnsLinked: boolean;
}
export interface ICommentData {
  key: string;
  id: string;
}
export interface InlineCommentData {
  inlineCommentText: string;
  highlightedTextContent: string;
  handleClick: boolean;
}

export interface IDDocSettings {
  zoomLevel: string;
}

export interface IUserDDocSettings {
  [ddocId: string]: IDDocSettings;
}

export interface ErrorResponseMessage {
  status: boolean;
  status_code: number;
  seq_id: string | null;
  is_handshake_response: boolean;
  err: string;
  err_detail: { [key: string]: any } | null;
}

export interface SuccessResponseMessage {
  status: boolean;
  status_code: number;
  seq_id: string | null;
  is_handshake_response: boolean;
  data: { [key: string]: any };
}

export type RequestResponse = ErrorResponseMessage | SuccessResponseMessage;
export type DisconnectHandler = (e: CloseEvent) => void;
export type ConnectHandler = (payload: {
  document_key: string;
  on_chain_document_id: number;
  roomId: string;
}) => void;
export type SequenceResponseCB = (data: RequestResponse) => void;

export interface SequenceToRequestMapValue {
  callback: SequenceResponseCB;
}

export type SequenceToRequestMap = {
  [key: string]: SequenceToRequestMapValue;
};
export enum SocketStatusEnum {
  CLOSED = 'CLOSED',
  CONNECTED = 'CONNECTED',
  CONNECTING = 'CONNECTING',
  DISCONNECTING = 'DISCONNECTING',
  DISCONNECTED = 'DISCONNECTED',
}

export interface EventMessage {
  type: string;
  event_type: string;
  event: { data: any; roomId: string };
}

export type EventHandler = (message: EventMessage) => void;

export interface PartialRequest {
  cmd: string;
  args: { [key: string]: any };
}

export interface RequestPayload extends PartialRequest {
  seq_id: string;
}

export interface SocketResponse {
  is_handshake_response: boolean;
  status: boolean;
  status_code: number;
}
export interface SendUpdateResponse extends SocketResponse {
  data: {
    agent_id: string;
    commit_cid: string | null;
    created_at: number;
    data: string;
    document_id: string;
    id: string;
    update_snapshot_ref: string | null;
    update_type: string;
  };
}

export interface AuthResponse extends SocketResponse {
  data: {
    document_id: string;
    document_key: string;
    document_type: string;
    on_chain_document_id: number;
    user_id: string;
    username: string;
  };
}

export interface DeleteUpdateResponse extends SocketResponse {
  data: {
    author: string;
    document_id: string;
    last_published_content_hash: string;
    update_ids: string[];
  };
}
export interface SetOnchainFileIdResponse extends SocketResponse {
  data: {
    author: string;
    document_id: string;
    on_chain_document_id: string;
  };
}
export interface SetKeyResponse extends SocketResponse {
  data: {
    author: string;
    document_id: string;
    document_key: string;
  };
}

export interface FetchDocUpdatesResponse extends SocketResponse {
  data: {
    history: Update[];
    document_key: string;
    total: number;
  };
}

export type Update = {
  author: string;
  last_published_content_hash: string | null;
  created_at: number;
  data: string;
  document_id: string;
  id: string;
};

export interface ISocketInitConfig {
  onConnect: ConnectHandler;
  onDisconnect: DisconnectHandler;
  onError: (err: string) => void;
  roomId: string;
  username: string;
  onWsEvent: EventHandler;
  portalAddress: string;
  didSecret: string;
}
export enum MigrationStatus {
  Pending = 'pending',
  InProgress = 'in-progress',
  Completed = 'completed',
  Failed = 'failed',
}

export type RtcKey = {
  key: Uint8Array;
  nonce: Uint8Array;
};
